import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Empty, Popconfirm, Spin, Tag } from "antd";
import { HistoryOutlined, UndoOutlined, WarningFilled } from "@ant-design/icons";
import ButtonComponent from "../../components/ButtonComponent/ButtonComponent";
import QuestionDetailView from "./QuestionDetailView";
import { errorToast, successToast } from "../../services/ToastHelper";
import { postApi } from "../../utils/apiService";
import { GET_QUESTION_HISTORY, RESTORE_QUESTION_VERSION } from "../../utils/apiPath";
import {
    changedFieldLabels,
    diffVersions,
    formatDateTime,
    parseSnapshot,
} from "./questionHelpers";

const ACTION_COLORS = {
    Created: "blue",
    Updated: "gold",
    Deleted: "red",
    Restored: "green",
};

/** The response shape is read tolerantly: { data: { versions: [] } } or a bare array. */
const readVersions = (data) => {
    const list = Array.isArray(data) ? data : data?.versions || data?.history || [];
    return [...list]
        .map((v) => ({ ...v, snapshot: parseSnapshot(v.snapshot ?? v.snapshotJson) }))
        .sort((a, b) => (b.version || 0) - (a.version || 0));
};

const QuestionHistoryTab = ({
    questionId,
    syllabusNameById,
    canRestore,
    attemptCount = 0,
    onRestored,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [versions, setVersions] = useState([]);
    const [updateCount, setUpdateCount] = useState(0);
    const [openVersion, setOpenVersion] = useState(null); // historyId being compared
    const [showFull, setShowFull] = useState(false);
    const [restoringId, setRestoringId] = useState(null);

    const fetchHistory = async () => {
        if (!questionId) return;
        setIsLoading(true);
        try {
            const { statusCode, data, message } = await postApi(GET_QUESTION_HISTORY, {
                questionId,
            });
            if (statusCode === 200) {
                const list = readVersions(data);
                setVersions(list);
                setUpdateCount(
                    data?.updateCount ?? list.filter((v) => v.action === "Updated").length
                );
                setOpenVersion(list.length > 1 ? list[0].historyId ?? list[0].id : null);
            } else {
                errorToast(message || "Could not load history");
            }
        } catch {
            errorToast("Could not load history");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
        setShowFull(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionId]);

    const rows = useMemo(
        () =>
            versions.map((v, i) => {
                const previous = versions[i + 1] || null;
                return {
                    ...v,
                    key: v.historyId ?? v.id ?? v.version,
                    isCurrent: i === 0,
                    previous,
                    changes: changedFieldLabels(v, previous, { syllabusNameById }),
                };
            }),
        [versions, syllabusNameById]
    );

    const handleRestore = async (row) => {
        setRestoringId(row.key);
        try {
            const { statusCode, message } = await postApi(RESTORE_QUESTION_VERSION, {
                questionId,
                historyId: row.historyId ?? row.id,
            });
            if (statusCode === 200) {
                successToast(message || "Question restored");
                await fetchHistory();
                onRestored?.();
            } else {
                errorToast(message || "Could not restore this version");
            }
        } catch {
            errorToast("Could not restore this version");
        } finally {
            setRestoringId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="qhistory__loading">
                <Spin />
            </div>
        );
    }

    if (!rows.length) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No changes recorded yet for this question"
            />
        );
    }

    const current = rows[0];

    return (
        <div className="qhistory">
            {attemptCount > 0 && (
                <div className="qhistory__attempts">
                    <WarningFilled />
                    <span>
                        {attemptCount} {attemptCount === 1 ? "student has" : "students have"} already
                        answered this question. They keep the version they answered, so their results
                        stay as they are — editing or restoring only changes what students get from
                        now on.
                    </span>
                </div>
            )}

            <div className="qhistory__summary">
                <HistoryOutlined />
                <span>
                    {updateCount === 0
                        ? "Never edited since it was created"
                        : `Updated ${updateCount} ${updateCount === 1 ? "time" : "times"}`}
                </span>
                <span className="qhistory__dot">·</span>
                <span>
                    Last change {formatDateTime(current.changedOn)}
                    {current.changedByName ? ` by ${current.changedByName}` : ""}
                    {current.changedByRole ? ` (${current.changedByRole})` : ""}
                </span>
            </div>

            <div className="qhistory__list">
                {rows.map((row) => {
                    const isOpen = openVersion === row.key;
                    const changes = row.previous
                        ? diffVersions(row.previous.snapshot, row.snapshot, { syllabusNameById })
                        : [];

                    return (
                        <div className={`qhistory__item ${isOpen ? "is-open" : ""}`} key={row.key}>
                            <div className="qhistory__head">
                                <div className="qhistory__when">
                                    <strong>{formatDateTime(row.changedOn)}</strong>
                                    <span className="qhistory__who">
                                        {row.changedByName || "Unknown user"}
                                        {row.changedByRole ? ` (${row.changedByRole})` : ""}
                                    </span>
                                </div>
                                <div className="qhistory__tags">
                                    <Tag color={ACTION_COLORS[row.action] || "default"}>
                                        {row.action || "Changed"}
                                    </Tag>
                                    {row.isCurrent && <Tag color="processing">Current</Tag>}
                                </div>
                            </div>

                            {row.changes.length > 0 && (
                                <div className="qhistory__changed">
                                    Changed: {row.changes.join(", ")}
                                </div>
                            )}

                            <div className="qhistory__actions">
                                {row.previous && (
                                    <ButtonComponent
                                        variant="secondary"
                                        onClick={() => setOpenVersion(isOpen ? null : row.key)}
                                    >
                                        {isOpen ? "Hide before / after" : "View before / after"}
                                    </ButtonComponent>
                                )}
                                {canRestore && !row.isCurrent && row.snapshot && (
                                    <Popconfirm
                                        title="Restore this version?"
                                        description={
                                            <div className="qhistory__confirm">
                                                <div>
                                                    The question goes back to how it was at this
                                                    point, and this becomes its current version.
                                                </div>
                                                {attemptCount > 0 && (
                                                    <div className="qhistory__confirmNote">
                                                        The {attemptCount}{" "}
                                                        {attemptCount === 1 ? "student" : "students"}{" "}
                                                        who already answered keep the version they
                                                        saw. Only new attempts get this one.
                                                    </div>
                                                )}
                                            </div>
                                        }
                                        okText="Restore"
                                        cancelText="Cancel"
                                        onConfirm={() => handleRestore(row)}
                                    >
                                        <ButtonComponent
                                            variant="secondary"
                                            disabled={restoringId === row.key}
                                        >
                                            <UndoOutlined /> Restore this version
                                        </ButtonComponent>
                                    </Popconfirm>
                                )}
                            </div>

                            {isOpen && (
                                <div className="qhistory__compare">
                                    {showFull ? (
                                        <div className="qhistory__panes">
                                            <div className="qhistory__pane">
                                                <div className="qhistory__paneHead">
                                                    Before — {formatDateTime(row.previous.changedOn)}
                                                </div>
                                                <QuestionDetailView
                                                    question={row.previous.snapshot}
                                                    syllabusNameById={syllabusNameById}
                                                    compact
                                                />
                                            </div>
                                            <div className="qhistory__pane">
                                                <div className="qhistory__paneHead">
                                                    After — {formatDateTime(row.changedOn)}
                                                </div>
                                                <QuestionDetailView
                                                    question={row.snapshot}
                                                    syllabusNameById={syllabusNameById}
                                                    compact
                                                />
                                            </div>
                                        </div>
                                    ) : changes.length === 0 ? (
                                        <div className="qhistory__noChange">
                                            No visible difference between these two versions.
                                        </div>
                                    ) : (
                                        <table className="qhistory__diff">
                                            <thead>
                                                <tr>
                                                    <th>What</th>
                                                    <th>Before</th>
                                                    <th>After</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {changes.map((c) => (
                                                    <tr key={c.field}>
                                                        <td className="qhistory__diffLabel">
                                                            {c.label}
                                                        </td>
                                                        <td className="qhistory__before">
                                                            {c.before}
                                                        </td>
                                                        <td className="qhistory__after">
                                                            {c.after}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    <button
                                        type="button"
                                        className="qhistory__toggleFull"
                                        onClick={() => setShowFull((v) => !v)}
                                    >
                                        {showFull
                                            ? "Show only what changed"
                                            : "Show the full question"}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

QuestionHistoryTab.propTypes = {
    questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    syllabusNameById: PropTypes.object,
    canRestore: PropTypes.bool,
    attemptCount: PropTypes.number,
    onRestored: PropTypes.func,
};

export default QuestionHistoryTab;
